import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Loader2, BadgeDollarSign, Mail, Info, CheckCircle2, Lock } from "lucide-react";
import { adminListPayments, adminMarkPayout, adminResendPaymentEmail } from "../services/payments";
import { fmtUSD } from "../lib/pricing";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScopeChips, matchesScope } from "../components/ScopeChips";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const TOKEN_KEY = "dcc_admin_token";
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

const ClassBadge = ({ p }) => {
  const { t } = useTranslation();
  const cls = p.classification;
  const color = cls === "REAL" ? "bg-emerald-600" : cls === "TEST" ? "bg-amber-500" : "bg-slate-400";
  const label = cls === "REAL" ? t("admin.real") : cls === "TEST" ? t("admin.test") : t("admin.demo");
  return <Badge className={`${color} text-white hover:${color}`} data-testid={`payment-tag-${p.session_id}`}>{label}</Badge>;
};

const Figure = ({ label, value, accent, testid }) => (
  <div className="min-w-0 rounded-lg bg-secondary/40 px-3 py-2" data-testid={testid}>
    <div className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`truncate text-sm font-bold ${accent || ""}`}>{value}</div>
  </div>
);

export default function AdminPayouts() {
  const { t } = useTranslation();
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [resendTarget, setResendTarget] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [scope, setScope] = useState("REAL");

  const confirmPayout = async () => {
    if (!payoutTarget) return;
    const sid = payoutTarget.session_id;
    await markPaid(sid);
    setPayoutTarget(null);
  };

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const list = await adminListPayments(tk);
      setPayments(list);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, tk);
    } catch (e) {
      setAuthed(false);
      toast.error(t("admin.invalidToken"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { if (token) load(token); /* eslint-disable-next-line */ }, []);

  const markPaid = async (sid) => {
    setBusy(sid);
    try {
      await adminMarkPayout(token, sid);
      toast.success(t("admin.markedPaid"));
      await load(token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin.actionFailed"));
    } finally {
      setBusy("");
    }
  };

  const confirmResend = async () => {
    if (!resendTarget) return;
    setResendBusy(true);
    try {
      const res = await adminResendPaymentEmail(token, resendTarget.session_id);
      if (res.sent) {
        toast.success(t("admin.resendSent"));
      } else {
        const d = res.detail || {};
        toast.error(`${t("admin.resendFailed")} — Lab: ${t(`admin.resendReason.${d.lab}`, d.lab || "?")}, Designer: ${t(`admin.resendReason.${d.designer}`, d.designer || "?")}`);
      }
      setResendTarget(null);
      await load(token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("admin.resendFailed"));
    } finally {
      setResendBusy(false);
    }
  };

  if (!authed) {
    return (
      <div className="mx-auto max-w-md px-4 py-20" data-testid="admin-login">
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold"><ShieldCheck className="h-6 w-6" /> {t("admin.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("admin.enterToken")}</p>
        <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Admin token" className="mt-4" data-testid="admin-token-input" />
        <Button className="mt-3 w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => load(token)} disabled={!token || loading} data-testid="admin-login-button">
          {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} {t("admin.enter")}
        </Button>
      </div>
    );
  }

  const visible = payments.filter((p) => matchesScope(p.classification, scope));
  const pending = visible.filter((p) => p.payment_status === "paid" && p.payout_status !== "paid");

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-payouts">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold"><BadgeDollarSign className="h-6 w-6 text-brand" /> {t("admin.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("admin.pendingCount", { count: pending.length })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ScopeChips value={scope} onChange={setScope} testid="payouts-scope" />
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => load(token)} disabled={loading} data-testid="admin-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {t("admin.refresh")}
          </Button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground">{payments.length === 0 ? t("admin.noPayments") : t("admin.noneForFilter")}</div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-2" data-testid="payments-grid">
          {visible.map((p) => {
            const eligible = p.payout_eligible;
            const paid = p.payment_status === "paid";
            const payoutDone = p.payout_status === "paid";
            return (
              <div key={p.session_id} className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md" data-testid={`payment-card-${p.session_id}`}>
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate font-heading font-semibold">{p.case_title || "—"}</span>
                      <ClassBadge p={p} />
                      {p.promo_applied && <Badge className="bg-brand text-white hover:bg-brand" data-testid={`promo-badge-${p.session_id}`}>{p.promo_code || "LAUNCH50"}</Badge>}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">{p.case_number || p.session_id.slice(0, 18)}</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge className={paid ? "bg-emerald-600 text-white hover:bg-emerald-600" : "bg-amber-500 text-white hover:bg-amber-500"} data-testid={`payment-status-${p.session_id}`}>
                      {t("admin.payment")}: {t(`admin.paymentStatus.${p.payment_status}`, p.payment_status)}
                    </Badge>
                    <Badge className={payoutDone ? "bg-emerald-600 text-white hover:bg-emerald-600" : ""} variant={payoutDone ? undefined : "secondary"} data-testid={`payout-status-${p.session_id}`}>
                      {t("admin.payout")}: {t(`admin.payoutStatus.${p.payout_status}`, p.payout_status)}
                    </Badge>
                  </div>
                </div>

                {/* Figures */}
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Figure label={t("admin.labPayment")} value={fmtUSD(p.amount)} testid={`fig-amount-${p.session_id}`} />
                  <Figure label={t("admin.commission")} value={fmtUSD(p.commission_amount)} testid={`fig-commission-${p.session_id}`} />
                  <Figure label={t("admin.net")} value={fmtUSD(p.designer_net)} accent="text-emerald-700" testid={`fig-net-${p.session_id}`} />
                </div>

                {/* Parties + case status */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="truncate"><span className="text-muted-foreground">{t("admin.lab")}: </span>{p.lab_name || p.lab_id || "—"}</span>
                  <span className="truncate"><span className="text-muted-foreground">{t("admin.designer")}: </span>{p.designer_name || p.designer_id || "—"}</span>
                  <span className="flex items-center gap-1"><span className="text-muted-foreground">{t("admin.caseStatus")}: </span>
                    <Badge variant="secondary" data-testid={`case-status-${p.session_id}`}>{p.case_status || t("admin.unknown")}</Badge>
                  </span>
                </div>

                {/* Actions */}
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setDetailTarget(p)} data-testid={`details-${p.session_id}`}>
                    <Info className="mr-1.5 h-3.5 w-3.5" /> {t("admin.details")}
                  </Button>
                  {paid && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setResendTarget(p)} data-testid={`resend-email-${p.session_id}`}>
                      <Mail className="mr-1.5 h-3.5 w-3.5" /> {t("admin.resendEmail")}
                    </Button>
                  )}
                  {paid && !payoutDone && (
                    eligible ? (
                      <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => setPayoutTarget(p)} disabled={busy === p.session_id} data-testid={`mark-paid-${p.session_id}`}>
                        {busy === p.session_id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />} {t("admin.markPayoutPaid")}
                      </Button>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/60 px-3 py-1 text-[11px] text-muted-foreground" data-testid={`payout-locked-${p.session_id}`}>
                        <Lock className="h-3 w-3" /> {t("admin.payoutNotEligible", { status: p.case_status || t("admin.unknown") })}
                      </span>
                    )
                  )}
                  {payoutDone && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700" data-testid={`payout-done-${p.session_id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("admin.payoutStatus.paid")}
                    </span>
                  )}
                  {p.last_resend_at && (
                    <span className={`ml-auto text-[10px] ${p.last_resend_result === "sent" ? "text-emerald-600" : "text-red-600"}`} data-testid={`resend-status-${p.session_id}`}>
                      {t("admin.lastResend")}: {p.last_resend_result === "sent" ? t("admin.resendSent") : t("admin.resendFailed")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-xs text-muted-foreground">{t("admin.connectNote")}</p>

      {/* Details modal — secondary info */}
      <Dialog open={!!detailTarget} onOpenChange={(o) => !o && setDetailTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="payment-details-dialog">
          <DialogHeader>
            <DialogTitle>{t("admin.transactionDetails")}</DialogTitle>
            <DialogDescription>{detailTarget?.case_title || detailTarget?.case_number}</DialogDescription>
          </DialogHeader>
          {detailTarget && (
            <div className="max-h-[65vh] space-y-2 overflow-y-auto text-sm">
              {[
                [t("admin.caseStatus"), detailTarget.case_status || t("admin.unknown")],
                [t("admin.labPayment"), fmtUSD(detailTarget.amount)],
                [t("admin.commission"), fmtUSD(detailTarget.commission_amount)],
                [t("admin.net"), fmtUSD(detailTarget.designer_net)],
                [t("admin.labEmail"), detailTarget.lab_email || "—"],
                [t("admin.designerEmail"), detailTarget.designer_email || "—"],
                ["Session", detailTarget.session_id],
                [t("admin.paymentId"), detailTarget.stripe_payment_intent_id || "—"],
                ["Stripe mode", detailTarget.stripe_mode || "—"],
                [t("admin.createdAt"), fmtDate(detailTarget.created_at)],
                [t("admin.payoutPaidAt"), fmtDate(detailTarget.payout_paid_at)],
                [t("admin.notifiedResult"), detailTarget.paid_notified_detail || (detailTarget.paid_notified ? t("admin.resendSent") : "—")],
                [t("admin.lastResend"), detailTarget.last_resend_at ? `${fmtDate(detailTarget.last_resend_at)} · ${detailTarget.last_resend_detail || detailTarget.last_resend_result || ""}` : "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-border/60 py-1">
                  <span className="shrink-0 text-muted-foreground">{k}</span>
                  <span className="break-all text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDetailTarget(null)} data-testid="details-close">{t("admin.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend confirm dialog */}
      <Dialog open={!!resendTarget} onOpenChange={(o) => !o && setResendTarget(null)}>
        <DialogContent data-testid="resend-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("admin.resendConfirmTitle")}</DialogTitle>
            <DialogDescription>{t("admin.resendConfirmBody")}</DialogDescription>
          </DialogHeader>
          {resendTarget && (
            <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.case")}</span><span className="font-medium">{resendTarget.case_title || resendTarget.case_number || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.labEmail")}</span><span>{resendTarget.lab_email || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.designerEmail")}</span><span>{resendTarget.designer_email || "—"}</span></div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setResendTarget(null)} disabled={resendBusy} data-testid="resend-cancel">
              {t("common.cancel")}
            </Button>
            <Button className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={confirmResend} disabled={resendBusy} data-testid="resend-confirm">
              {resendBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} {t("admin.resendEmail")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Designer payout confirm dialog */}
      <Dialog open={!!payoutTarget} onOpenChange={(o) => !o && setPayoutTarget(null)}>
        <DialogContent data-testid="payout-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("admin.payoutConfirmTitle")}</DialogTitle>
            <DialogDescription data-testid="payout-confirm-body">
              {payoutTarget && t("admin.payoutConfirmBody", {
                amount: fmtUSD(payoutTarget.designer_net),
                designer: payoutTarget.designer_name || payoutTarget.designer_id || "—",
              })}
            </DialogDescription>
          </DialogHeader>
          {payoutTarget && (
            <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm">
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.case")}</span><span className="font-medium">{payoutTarget.case_title || payoutTarget.case_number || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.designer")}</span><span>{payoutTarget.designer_name || payoutTarget.designer_id || "—"}</span></div>
              <div className="flex justify-between gap-2"><span className="text-muted-foreground">{t("admin.net")}</span><span className="font-bold text-emerald-700">{fmtUSD(payoutTarget.designer_net)}</span></div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setPayoutTarget(null)} disabled={busy === payoutTarget?.session_id} data-testid="payout-cancel">
              {t("common.cancel")}
            </Button>
            <Button className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={confirmPayout} disabled={busy === payoutTarget?.session_id} data-testid="payout-confirm">
              {busy === payoutTarget?.session_id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} {t("admin.payoutConfirmCta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
